<template>
  <!--
    Offer (REWARDS) card dispatcher. The public props/emits are unchanged; the
    two large template branches now live in dedicated presentational bodies and
    all derived state + gating wiring lives in `useOfferCard`. The new channel
    layout (flag `enable_new_channel_layout`) renders the Figma AS-Monaco body;
    the flag-OFF path renders the legacy body so the old channel layout is
    untouched.
  -->
  <OfferCardNewBody
    v-if="newLayout"
    :offer="offer"
    :status-label="statusLabel"
    :description-label="descriptionLabel"
    :cta-label="ctaLabel"
    :new-cta-classes="newCtaClasses"
    :card-aria-label="cardAriaLabel"
    :has-claimed="hasClaimed"
    :is-sold-out="isSoldOut"
    :is-gated="isGated"
    :criterion-pills="criterionPills"
    :icon-image-overrides="iconImageOverrides"
    :can-manage-offers="canManageOffers"
    @cta-click="onCtaClick"
    @deleted="emit('deleted')"
  />
  <OfferCardLegacyBody
    v-else
    :offer="offer"
    :cta-label="ctaLabel"
    :cta-classes="ctaClasses"
    :cta-content-class="ctaContentClass"
    :card-aria-label="cardAriaLabel"
    :has-claimed="hasClaimed"
    :is-sold-out="isSoldOut"
    :is-gated="isGated"
    :tooltip-label="tooltipLabel"
    :gating-criterion-icons="gatingCriterionIcons"
    :icon-image-overrides="iconImageOverrides"
    :can-manage-offers="canManageOffers"
    @cta-click="onCtaClick"
    @deleted="emit('deleted')"
  />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const OfferCardFragment = gql(/* GraphQL */ `
  fragment OfferCardData on Offer {
    id
    name
    description
    short_description
    image_url
    user_has_claimed
    authorization_rule_group {
      id
      ...AuthorizationRuleGroupForGating
    }
    poolStats {
      available
    }
  }
`);

export type OfferCardData = ResultOf<typeof OfferCardFragment>;
</script>

<script setup lang="ts">
import { useFeatureFlags } from "@/composables/use-feature-flags";
import { useOfferCard } from "./use-offer-card";
import OfferCardLegacyBody from "./offer-card-legacy-body.vue";
import OfferCardNewBody from "./offer-card-new-body.vue";

const props = defineProps<{
  offer: OfferCardData;
}>();

const emit = defineEmits<{
  openDetail: [];
  deleted: [];
}>();

const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

const {
  onCtaClick,
  isGated,
  hasClaimed,
  isSoldOut,
  ctaLabel,
  ctaClasses,
  ctaContentClass,
  newCtaClasses,
  cardAriaLabel,
  statusLabel,
  descriptionLabel,
  tooltipLabel,
  criterionPills,
  gatingCriterionIcons,
  iconImageOverrides,
  canManageOffers,
} = useOfferCard(
  () => props.offer,
  () => emit("openDetail"),
);
</script>
